import { useEffect, useState, useCallback } from 'react';
import { realtimeService } from '../services/realtimeService';
import { useAuth } from '../contexts/AuthContext';

/**
 * Custom hook for real-time user notifications
 * Subscribes to user-specific notification channel
 * 
 * @returns {Object} { notifications, unreadCount, markAsRead, clearAll }
 */
export const useRealtimeNotifications = () => {
  const { user, profile } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);

  // Handle new notification
  const handleNewNotification = useCallback((notification) => {
    console.log('[useRealtimeNotifications] New notification:', notification);
    
    // Transform notification to camelCase
    const transformedNotification = {
      id: notification?.id,
      recipientId: notification?.recipient_id,
      notificationType: notification?.notification_type,
      title: notification?.title,
      message: notification?.message,
      priority: notification?.priority,
      isRead: notification?.is_read,
      metadata: notification?.metadata,
      createdAt: notification?.created_at
    };

    setNotifications(prev => [transformedNotification, ...prev]);
    
    if (!transformedNotification?.isRead) {
      setUnreadCount(prev => prev + 1);
    }

    // Show browser notification if permission granted
    if (Notification.permission === 'granted') {
      new Notification(transformedNotification.title || 'New Notification', {
        body: transformedNotification.message,
        icon: '/favicon.ico',
        tag: transformedNotification.id
      });
    }
  }, []);

  // Mark notification as read
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev =>
      prev?.map(notif =>
        notif?.id === notificationId ? { ...notif, isRead: true } : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);

  // Clear all notifications
  const clearAll = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
  }, []);

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  // Subscribe to real-time notifications
  useEffect(() => {
    if (!user?.id) {
      console.warn('[useRealtimeNotifications] Cannot subscribe: no user ID');
      return;
    }

    console.log(`[useRealtimeNotifications] Subscribing for user ${user?.id}`);
    setIsConnected(true);

    const unsubscribe = realtimeService?.subscribeToUserNotifications(
      user?.id,
      handleNewNotification
    );

    return () => {
      console.log('[useRealtimeNotifications] Unsubscribing');
      setIsConnected(false);
      unsubscribe();
    };
  }, [user?.id, handleNewNotification]);

  return {
    notifications,
    unreadCount,
    isConnected,
    markAsRead,
    clearAll
  };
};

export default useRealtimeNotifications;
